import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
} from '@mui/material';
import { useAuth } from '../AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminMenu from './AdminMenu';
import exifr from 'exifr';
import DashboardLeaderboard from './DashboardLeaderboard';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import MenuItem from '@mui/material/MenuItem';
import { fetchDashboardInitData, fetchCatchFormData } from '../api';
import { API_URL } from '../config';

// Robust EXIF GPS extractor with fallbacks
const extractGpsFromFile = async (file) => {
  try {
    // First, use exifr.gps which returns decimal degrees when possible
    const gps = await exifr.gps(file);
    if (gps && Number.isFinite(gps.latitude) && Number.isFinite(gps.longitude)) {
      return { latitude: gps.latitude, longitude: gps.longitude };
    }
    // Fallback: parse full metadata and attempt alternative tags
    const meta = await exifr.parse(file);
    const latCandidates = [
      meta?.latitude,
      meta?.GPSLatitude,
      meta?.xmp?.GPSLatitude,
    ].filter((v) => v !== undefined && v !== null);
    const lonCandidates = [
      meta?.longitude,
      meta?.GPSLongitude,
      meta?.xmp?.GPSLongitude,
    ].filter((v) => v !== undefined && v !== null);
    const candidateLat = Array.isArray(latCandidates) ? latCandidates[0] : undefined;
    const candidateLon = Array.isArray(lonCandidates) ? lonCandidates[0] : undefined;
    const latRef = meta?.GPSLatitudeRef || meta?.xmp?.GPSLatitudeRef || meta?.latitudeRef;
    const lonRef = meta?.GPSLongitudeRef || meta?.xmp?.GPSLongitudeRef || meta?.longitudeRef;
    const toNumber = (val) => {
      if (val == null) return NaN;
      if (typeof val === 'number') return val;
      if (Array.isArray(val)) {
        // DMS array [deg, min, sec] -> decimal
        const [d = 0, m = 0, s = 0] = val;
        return Number(d) + Number(m) / 60 + Number(s) / 3600;
      }
      // Strings with comma decimal (e.g., "47,075")
      if (typeof val === 'string') {
        const normalized = val.replace(',', '.');
        const n = Number(normalized);
        return Number.isFinite(n) ? n : NaN;
      }
      const n = Number(val);
      return Number.isFinite(n) ? n : NaN;
    };
    const latNum = toNumber(candidateLat);
    const lonNum = toNumber(candidateLon);
    if (Number.isFinite(latNum) && Number.isFinite(lonNum)) {
      const applyRef = (num, ref) => {
        if (!ref) return num;
        const r = String(ref).toUpperCase();
        // South and West should be negative
        if (r === 'S' || r === 'W') return -Math.abs(num);
        return Math.abs(num);
      };
      return { latitude: applyRef(latNum, latRef), longitude: applyRef(lonNum, lonRef) };
    }
  } catch (err) {
    // ignore, handled by caller
  }
  return null;
};

export default function DashboardPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const groups = Array.isArray(user?.groups) ? user.groups : [];
  // Only show non-admin groups for selection
  const userGroups = groups.filter(g => g.group_name.toLowerCase() !== 'admins');
  const [selectedGroup, setSelectedGroup] = useState(userGroups[0]?.group_id || '');
  const [catchDialogOpen, setCatchDialogOpen] = useState(false);
  const [species, setSpecies] = useState('');
  const [size, setSize] = useState('');
  const [bait, setBait] = useState('');
  const [rod, setRod] = useState('');
  const [image, setImage] = useState(null);
  const [location, setLocation] = useState('');
  const [geoLocation, setGeoLocation] = useState(null);
  const [preview, setPreview] = useState(null);
  const [cameraSupported, setCameraSupported] = useState(false);
  const [cameraMessage, setCameraMessage] = useState('');
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [cameraFlash, setCameraFlash] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [locationResolving, setLocationResolving] = useState(false);
  const [locationInfo, setLocationInfo] = useState('');
  const [fileDims, setFileDims] = useState({ width: null, height: null });
  const [fileSize, setFileSize] = useState(null);
  const [speciesOptions, setSpeciesOptions] = useState([]);
  const [rodOptions, setRodOptions] = useState([]);
  const [baitOptions, setBaitOptions] = useState([]);
  const [leaderboardRefreshKey, setLeaderboardRefreshKey] = useState(0);
  const routerLocation = useLocation();

  // Site admin: user is admin in the 'Admins' group
  const isAdmin = groups.some(g => g.role === 'admin' && (g.group_name || '').toLowerCase() === 'admins');

  // Load species only on mount
  useEffect(() => {
    const authToken = token || localStorage.getItem('token');
    if (!authToken) return;
    fetchDashboardInitData(authToken)
      .then(data => {
        setSpeciesOptions(Array.isArray(data.species) ? data.species : []);
      })
      .catch(err => {
        console.error('Failed to load species:', err);
        setSpeciesOptions([]);
      });
  }, [token]);

  // Lazy-load tackle data when catch drawer opens
  const loadTackleData = async () => {
    if (rodOptions.length > 0 || baitOptions.length > 0) return; // Already loaded
    const authToken = token || localStorage.getItem('token');
    if (!authToken) return;
    try {
      const data = await fetchCatchFormData(authToken);
      setRodOptions(Array.isArray(data.rods) ? data.rods : []);
      setBaitOptions(Array.isArray(data.baits) ? data.baits : []);
    } catch (err) {
      console.error('Failed to load tackle data:', err);
      setRodOptions([]);
      setBaitOptions([]);
    }
  };

  // Reverse geocode helper to get nearest city/town from coordinates
  const reverseGeocode = async (latitude, longitude) => {
    try {
      setLocationResolving(true);
      const url = `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=jsonv2&addressdetails=1`;
      const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!res.ok) {
        setLocationInfo('Reverse geocoding failed.');
        return null;
      }
      const data = await res.json();
      const addr = data?.address || {};
      const primary = addr.city || addr.town || addr.village || addr.hamlet || addr.suburb || data?.name;
      const region = addr.state || addr.region || addr.county;
      const country = addr.country;
      const parts = [primary, region, country].filter(Boolean);
      const result = parts.length ? parts.join(', ') : null;
      if (!result) setLocationInfo('No city/town found for coordinates.');
      return result;
    } catch {
      setLocationInfo('Reverse geocoding error.');
      return null;
    }
    finally { setLocationResolving(false); }
  };

  const setLocationFromGeo = async ({ latitude, longitude }) => {
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      setLocationInfo('Invalid coordinates for location.');
      return;
    }
    setGeoLocation({ latitude, longitude });
    setLocationResolving(true);
    const human = await reverseGeocode(latitude, longitude);
    if (human) {
      setLocation(human);
    } else {
      setLocation(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
    }
    setLocationResolving(false);
  };

  // Try to automatically capture device location when opening the Add Catch dialog
  useEffect(() => {
    if (!catchDialogOpen) return;
    loadTackleData(); // Lazy-load tackle when form opens
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        // Only auto-fill if user has not entered a location yet AND no EXIF-based geo is present
        if (!location || !location.trim()) {
          if (!geoLocation || typeof geoLocation.latitude !== 'number') {
            setLocationFromGeo({ latitude, longitude });
          }
        }
      },
      () => {
        // Ignore errors silently; user can still type location manually
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [catchDialogOpen]);

  // Handle deep-link state from TabNav to open Add Catch dialog and camera
  useEffect(() => {
    if (!routerLocation || !routerLocation.state) return;
    const { openCatchDialog, openCamera } = routerLocation.state || {};
    if (openCatchDialog) {
      setCatchDialogOpen(true);
    }
    if (openCamera) {
      // Slight delay to ensure dialog state settles
      setTimeout(() => setCameraDialogOpen(true), 0);
    }
    // Clear the state to avoid re-triggering on re-render
    if (openCatchDialog || openCamera) {
      navigate(routerLocation.pathname, { replace: true, state: {} });
    }
  }, [routerLocation, navigate]);

  // Open and manage the camera when the dedicated camera dialog is open
  useEffect(() => {
    if (!cameraDialogOpen) {
      setCameraSupported(false);
      setCameraMessage('');
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      return;
    }

    if (typeof navigator === 'undefined' || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraSupported(false);
      setCameraMessage('Kamera wird von diesem Gerät/Browser nicht unterstützt.');
      return;
    }

    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        streamRef.current = stream;
        setCameraSupported(true);
        setCameraMessage('');
      } catch (err) {
        setCameraSupported(false);
        if (err && err.name === 'NotAllowedError') {
          setCameraMessage('Kamerazugriff abgelehnt. Bitte Galerie verwenden.');
        } else {
          setCameraMessage('Kamera konnte nicht gestartet werden. Bitte Galerie verwenden.');
        }
      }
    };

    startCamera();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, [cameraDialogOpen]);

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    setImage(file || null);
    if (file) {
      setFileSize(file.size || null);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
        try {
          const img = new Image();
          img.onload = () => setFileDims({ width: img.naturalWidth, height: img.naturalHeight });
          img.src = reader.result;
        } catch {}
      };
      reader.readAsDataURL(file);
      // Try to read GPS from EXIF and set location accordingly
      try {
        // Determine if any EXIF is present and keep meta for non-GPS fallbacks
        let meta = null;
        try {
          meta = await exifr.parse(file).catch(() => null);
        } catch {}
        const gps = await extractGpsFromFile(file);
        if (gps && Number.isFinite(gps.latitude) && Number.isFinite(gps.longitude)) {
          await setLocationFromGeo({ latitude: gps.latitude, longitude: gps.longitude });
          setLocationInfo('EXIF location detected.');
        } else {
          // Fallback: derive human location from XMP textual tags if present
          let human = '';
          try {
            const x = meta?.xmp || {};
            const city = x.City || x.city || x.Location || x.location || x['photoshop:City'];
            const region = x.State || x.Region || x.Province || x['photoshop:State'];
            const country = x.Country || x['Country-PrimaryLocationName'] || x['photoshop:Country'];
            const parts = [city, region, country].filter(Boolean);
            if (parts.length) {
              human = parts.join(', ');
            }
          } catch {}
          if (human) {
            setLocation(human);
            setLocationInfo('No GPS in EXIF. Using XMP place tags.');
          } else {
            setLocationInfo('No EXIF GPS found in image.');
          }
        }
      } catch (_) {
        setLocationInfo('Failed to read EXIF GPS.');
      }
    } else {
      setPreview(null);
    }
  };

  const openImagePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAddImageClick = () => {
    if (typeof navigator !== 'undefined' && navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      setCameraDialogOpen(true);
    } else {
      openImagePicker();
    }
  };

  const handleCapturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;

    const canvas = document.createElement('canvas');
    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, width, height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `catch-${Date.now()}.jpg`, { type: 'image/jpeg' });
        setImage(file);
        const previewUrl = URL.createObjectURL(blob);
        setPreview(previewUrl);
        // Record basic file info for UI
        try {
          setFileSize(file.size || null);
          setFileDims({ width, height });
        } catch {}
        // Try to capture precise geo at the moment of taking the photo
        if (navigator && navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              const { latitude, longitude } = pos.coords;
              setLocationFromGeo({ latitude, longitude });
            },
            () => {
              // Ignore errors; existing location (if any) remains
            },
            { enableHighAccuracy: true, timeout: 8000 }
          );
        }
      },
      'image/jpeg',
      0.92
    );
  };

  const resetCatchForm = () => {
    setSpecies('');
    setSize('');
    setBait('');
    setRod('');
    setLocation('');
    setGeoLocation(null);
    setImage(null);
    setPreview(null);
    setFileDims({ width: null, height: null });
    setFileSize(null);
  };

  const handleSubmitCatch = async () => {
    if (!user?.id) {
      setSubmitError('User not found');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    setSubmitSuccess('');
    try {
      const formData = new FormData();
      formData.append('user_id', user.id);
      formData.append('species', species);
      formData.append('size', size);
      formData.append('bait', bait);
      formData.append('rod', rod);
      formData.append('location', location);
      if (geoLocation && typeof geoLocation.latitude === 'number' && typeof geoLocation.longitude === 'number') {
        formData.append('latitude', geoLocation.latitude);
        formData.append('longitude', geoLocation.longitude);
      }
      if (image) formData.append('image', image);

      const authToken = token || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/catches`, {
        method: 'POST',
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        body: formData,
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to submit catch');
      }
      setSubmitSuccess('Catch submitted!');
      setLeaderboardRefreshKey((prev) => prev + 1);
      resetCatchForm();
      setCatchDialogOpen(false);
    } catch (err) {
      setSubmitError(err.message || 'Error submitting catch');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth={false} disableGutters sx={{ width: '100vw', px: 0, mx: 0, mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
        <Box
          sx={{
            width: '100%',
            maxWidth: 600,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography
            variant="h4"
            align="center"
            sx={{
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: 'none',
            }}
          >
            TackleTribe.
          </Typography>
          <Typography variant="subtitle2" align="center" color="text.secondary">
            Deine Crew. Deine Fänge. Dein Tackle.
          </Typography>
          <Avatar
            src={(() => {
              const pic = user?.profile_picture;
              if (!pic) return undefined;
              if (pic.startsWith('http://') || pic.startsWith('https://')) return pic;
              const normalized = pic.startsWith('/') ? pic.slice(1) : pic;
              return `${API_URL}/${normalized}`;
            })()}
            sx={{ width: 72, height: 72, mb: 1 }}
          />
          <Typography variant="h4" gutterBottom align="center">
            Welcome, {user?.nickname || user?.email || 'Fisher'}!
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          mt: 4,
          width: '100vw',
          alignItems: 'center',
          pb: 4,
        }}
      >
        {selectedGroup && (
          <DashboardLeaderboard
            groupId={selectedGroup}
            groups={userGroups}
            selectedGroup={selectedGroup}
            refreshKey={leaderboardRefreshKey}
            onGroupChange={setSelectedGroup}
          />
        )}
      </Box>

      <Dialog open={catchDialogOpen} onClose={() => !submitting && setCatchDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Add Catch</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ mt: 1, textAlign: 'center' }}>
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 480,
                  maxHeight: 320,
                  borderRadius: 16,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  cursor: 'pointer',
                }}
                onClick={handleAddImageClick}
              >
                {preview ? (
                  <img
                    src={preview}
                    alt="preview"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <CameraAltIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                )}
              </Box>
              {preview && (
                <Box sx={{ mt: 1 }}>
                  {locationResolving ? (
                    <Typography variant="caption" color="text.secondary">Detecting location…</Typography>
                  ) : location ? (
                    <Typography variant="body2" color="text.secondary">Location: {location}</Typography>
                  ) : null}
                  {(fileDims.width || fileDims.height || fileSize) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {fileDims.width && fileDims.height ? `${fileDims.width}×${fileDims.height}px` : ''}
                      {fileDims.width && fileDims.height && fileSize ? ' · ' : ''}
                      {typeof fileSize === 'number' ? `${(fileSize / (1024 * 1024)).toFixed(2)} MB` : ''}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            {speciesOptions.length > 0 ? (
              <TextField
                select
                label="Species"
                fullWidth
                value={species}
                onChange={(e) => setSpecies(e.target.value)}
                required
              >
                {speciesOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.name}>
                    {opt.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                label="Species"
                fullWidth
                value={species}
                onChange={(e) => setSpecies(e.target.value)}
                required
              />
            )}
            <TextField
              label="Size (cm)"
              type="number"
              fullWidth
              value={size}
              onChange={(e) => setSize(e.target.value)}
              required
            />
            {baitOptions.length > 0 ? (
              <TextField
                select
                label="Bait"
                fullWidth
                value={bait}
                onChange={(e) => setBait(e.target.value)}
              >
                {baitOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.name}>
                    {opt.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                label="Bait"
                fullWidth
                value={bait}
                onChange={(e) => setBait(e.target.value)}
              />
            )}
            {rodOptions.length > 0 ? (
              <TextField
                select
                label="Rod"
                fullWidth
                value={rod}
                onChange={(e) => setRod(e.target.value)}
              >
                {rodOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.name}>
                    {opt.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                label="Rod"
                fullWidth
                value={rod}
                onChange={(e) => setRod(e.target.value)}
              />
            )}
            
            {submitError && (
              <Typography color="error" variant="body2">
                {submitError}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !submitting && setCatchDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitCatch}
            variant="contained"
            disabled={submitting || !species || !size}
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fullscreen camera dialog for a larger preview, similar to Instagram-style capture */}
      <Dialog
        open={cameraDialogOpen}
        onClose={() => setCameraDialogOpen(false)}
        fullScreen
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <IconButton
              aria-label="Kamera schließen"
              onClick={() => setCameraDialogOpen(false)}
              edge="end"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              bgcolor: 'black',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            {cameraFlash && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  bgcolor: '#ffffff',
                  opacity: 0.8,
                  pointerEvents: 'none',
                  animation: 'camera-flash 150ms ease-out forwards',
                }}
                onAnimationEnd={() => setCameraFlash(false)}
              />
            )}
          </Box>
          <Box sx={{ p: 2, textAlign: 'center' }}>
            {!cameraSupported && cameraMessage && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                {cameraMessage}
              </Typography>
            )}
            {cameraSupported ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.3)',
                    mr: 2,
                    bgcolor: preview ? 'transparent' : 'rgba(255,255,255,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => { openImagePicker(); setCameraDialogOpen(false); }}
                >
                  {preview ? (
                    <img
                      src={preview}
                      alt="letztes Foto"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Galerie
                    </Typography>
                  )}
                </Box>
                <IconButton
                  aria-label="Foto aufnehmen"
                  onClick={() => {
                    setCameraFlash(true);
                    handleCapturePhoto();
                    setCameraDialogOpen(false);
                  }}
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    bgcolor: 'background.paper',
                    border: '2px solid rgba(255,255,255,0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CameraAltIcon fontSize="large" />
                </IconButton>
                <Box sx={{ width: 64 }} />
              </Box>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  openImagePicker();
                  setCameraDialogOpen(false);
                }}
              >
                Galerie öffnen
              </Button>
            )}
          </Box>
        </Box>
      </Dialog>

      {/* Hidden file input used for gallery selection / fallback */}
      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={handleImageChange}
      />

      <Snackbar
        open={!!submitError}
        autoHideDuration={4000}
        onClose={() => setSubmitError('')}
      >
        <Alert severity="error" onClose={() => setSubmitError('')}>
          {submitError}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!locationInfo}
        autoHideDuration={3000}
        onClose={() => setLocationInfo('')}
      >
        <Alert severity="info" onClose={() => setLocationInfo('')}>
          {locationInfo}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!submitSuccess}
        autoHideDuration={4000}
        onClose={() => setSubmitSuccess('')}
      >
        <Alert severity="success" onClose={() => setSubmitSuccess('')}>
          {submitSuccess}
        </Alert>
      </Snackbar>
    </Container>
  );
}
